<template>
  <div>
    <div v-if="!loading && !isStaffAndPayrollEmpty" class="mb-3">
      <v-app>
        <div class="d-flex justify-end">
          <erase-icon-button
            v-if="removeButtonVisible"
            class="mr-2"
            @click="onDelete"
          />
          <business-guide-search v-model="searchValue" />
        </div>
      </v-app>
    </div>

    <ib-list-view-placeholder
      v-if="!loading && isStaffAndPayrollEmpty"
      :disabled="!canEdit || disabled"
      :title="$t('addPayrollExpense')"
      @click="onAddStaffAndPayroll"
    />
    <template v-else>
      <el-row
        v-if="!loading"
        :gutter="20"
        class="d-flex flex-wrap"
      >
        <el-col :md="12">
          <add-new-card
            :disabled="!canEdit"
            class="mb-2 py-md-0"
            :title="$tc('addPayrollExpense')"
            horizontal
            min-height="62px"
            @click="onAddStaffAndPayroll"
          />
        </el-col>

        <el-col
          v-for="staffAndPayroll in filterPayrollExpenses"
          :key="staffAndPayroll.id"
          class="d-flex mb-2"
          :md="12"
        >
          <div class="d-flex w-100">
            <set-up-and-projections-card
              class="w-100"
              :entity="staffAndPayroll"
              :name="staffAndPayroll.name"
              :date="staffPayrollDate(staffAndPayroll)"
              :amount="getFormattedAmount(staffAndPayroll.average)"
              :frequency="$t('pages.businessGuide.monthlyAverage')"
              :type="staffAndPayroll.type"
              :disabled="!canEdit"
              view="list"
              @edit="onEditStaffAndPayroll"
              @copy="onCopyStaffAndPayroll"
              @delete="onDeleteStaffAndPayroll"
            />
          </div>
        </el-col>
      </el-row>
    </template>

    <!-- Add Staff And Payroll-->
    <dialog-add-staff
      :dialog-add-staff-visible="dialogAddStaffVisible"
      :staff-and-payroll-action="staffAndPayrollAction"
      @close-dialog-add-staff="onCloseDialogAddStaff"
      @close-dialog-on-button="onCloseDialogAddStaff"
      @on-update-staff-and-payroll-view="onUpdate"
    />
    <!-- /Add Staff And Payroll -->
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AddNewCard from '@/components/_v2/AddNewCard.vue'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import DialogAddStaff from '@/views/Home/StoryMode/SetUp/StaffAndPayroll/Dialogs/DialogAddStaff.vue'
import EraseIconButton from '@/components/_v2/EraseIconButton.vue'
import EventBus from '@/event-bus'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinCurrencies from '@/mixins/currencies'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import SetUpAndProjectionsCard from '@/views/Home/StoryMode/Components/Cards/SetUpAndProjectionsCard.vue'

export default {
  name: 'PayrollExpensesList',

  components: { EraseIconButton, DialogAddStaff, BusinessGuideSearch, AddNewCard, SetUpAndProjectionsCard },

  mixins: [MixinApiCalculations, MixinCurrencies, MixinIdeaRoles, MixinDeleteIdeaExamples],

  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searchValue: '',
      loading: false,
      dialogAddStaffVisible: false,
      staffAndPayrollAction: null
    }
  },

  computed: {
    ...mapState(['idea']),

    ...mapGetters('idea', {
      staffAndPayrolls: 'getStaffAndPayrolls'
    }),

    filterPayrollExpenses () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.staffAndPayrolls.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
      }

      return this.staffAndPayrolls
    },

    isStaffAndPayrollEmpty () {
      return !this.staffAndPayrolls.length
    },

    removeButtonVisible () {
      return this.canEdit && this.staffAndPayrollIHasItemsFromTemplate
    }
  },

  mounted () {
    EventBus.$on('add-edit', () => {
      this.onShowAddNewDialog()
    })

    EventBus.$on('remove-examples', () => {
      this.deleteExamples()
    })

    EventBus.$on('reload', () => {
      this.onUpdate()
    })
  },

  beforeDestroy () {
    EventBus.$off('add-edit')
    EventBus.$off('remove-examples')
    EventBus.$off('reload')
  },

  methods: {
    ...mapActions('idea', {
      fetchStaffAndPayroll: 'getStaffAndPayroll',
      deleteStaffAndPayroll: 'deleteStoryModeStaffAndPayroll',
      deleteIdeaExamples: 'deleteIdeaExamples'
    }),

    onShowAddNewDialog () {
      if (!this.canEdit) return
      this.getStaffAndPayroll()
      this.dialogAddStaffVisible = true
    },

    onAddStaffAndPayroll () {
      if (!this.canEdit) return
      this.dialogAddStaffVisible = true
    },

    onCloseDialogAddStaff () {
      this.dialogAddStaffVisible = false
      this.staffAndPayrollAction = null
    },

    onEditStaffAndPayroll (staffAndPayroll) {
      this.staffAndPayrollAction = Object.assign({}, staffAndPayroll)
      this.dialogAddStaffVisible = true
    },

    onCopyStaffAndPayroll (staffAndPayroll) {
      this.staffAndPayrollAction = Object.assign({}, staffAndPayroll)
      this.staffAndPayrollAction.id = 0

      this.dialogAddStaffVisible = true
    },

    onDeleteStaffAndPayroll (staffAndPayroll) {
      this.emitLoading(true)
      this.deleteStaffAndPayroll(staffAndPayroll.id)
        .then(async () => {
          await this.getStaffAndPayroll()
          await this.getTotalCosts()
          await this.getProfitAndCashFlow()
        })
        .finally(_ => {
          this.emitLoading(false)
        })
    },

    onUpdate () {
      this.emitLoading(true)
      Promise.all([
        this.getStaffAndPayroll(),
        this.getTotalCosts(),
        this.getProfitAndCashFlow()
      ])
        .then(() => {
          this.emitLoading(false)
        })
        .finally(() => {
          this.emitLoading(false)
        })
    },

    deleteExamples () {
      this.deleteExamplesMixin('staff-and-payrolls')
        .then(_ => { this.onUpdate() })
    },

    staffPayrollDate (staffPayroll) {
      return `${this.getFormattedDate(staffPayroll.salaryStarts)} - ${this.getFormattedDate(staffPayroll.salaryEnds)}`
    },

    getFormattedDate (date) {
      return this.$moment(date).format('MMM YYYY')
    },

    emitLoading (value) {
      this.loading = value
      EventBus.$emit('loading', value)
      this.$emit('loading', value)
    }
  }
}
</script>
